/**
 * The top bar's CRUMB — `Group › Page` (#2502 §5.1; the design's `.crumb`).
 *
 * Where the person is, in the words the governed navigation already uses: the
 * section (a space, on the portal) and the page. It names; it does not
 * navigate — the rail is the navigation, and a crumb whose ancestor is a
 * section heading has nowhere to link to.
 *
 * Under pressure the PAGE outranks its section (anatomy §1b): the current
 * crumb never shrinks, the ancestor truncates first, and a host that has less
 * room still passes no trail at all rather than a clipped node with an
 * orphaned chevron. Data-free: the host resolves both labels.
 *
 * The console's top bar still draws its own crumb (`apps/console/src/app/
 * Topbar.tsx`); its adoption of this piece is a recorded hand-off (#2502 A1).
 */
import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import s from './Crumb.module.css'

export function Crumb({ trail = [], current, label = 'Breadcrumb' }: {
  /** Ancestors, outermost first — the section, for a portal page. */
  trail?: readonly ReactNode[]
  /** The page on screen. */
  current: ReactNode
  /**
   * The landmark's accessible name, in the host's language (#2624 L1).
   * Default "Breadcrumb", the name existing hosts already announce.
   */
  label?: string
}) {
  return (
    <nav className={s.crumb} aria-label={label} data-testid="crumb">
      <ol className={s.list}>
        {trail.map((node, i) => (
          <li key={i} className={s.ancestor}>
            <span className={s.label}>{node}</span>
            <ChevronRight className={s.sep} size={10} strokeWidth={2.6} aria-hidden focusable="false" />
          </li>
        ))}
        {/* NOT `aria-current`: the rail's destination already carries the
            page's one "current" statement, and two elements claiming to be the
            current page is a second answer to one question (nav-boundary pins
            exactly one). The crumb's last item is the page by position. */}
        <li className={s.current} data-current="">{current}</li>
      </ol>
    </nav>
  )
}
